import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './saleDot.css';

// A sold home on a price chart (the listing popup's and the area page's AreaPriceChart, the place card's
// sparkline): a dot that grows under the pointer and shows a small card with what it sold for — when, the
// price, kr/m², rooms and how prices have moved since. Hover or focus shows it; on touch a tap toggles it and
// a tap anywhere else closes it. The card is rendered into document.body at the dot's screen position, so the
// chart's or popup's overflow never clips it.
//
// sale:  {date?, period, price?, sqmPrice, rooms?}
// since: optional {pct, label} — "Området +12 % sedan dess" / "Idag +18 % per m²"
// style: the dot's position over the chart (left/top in %)

const NBSP = ' ';
const GAP = 10;
const EDGE = 8;

const formatInt = (value, swedish) => Math.round(value).toLocaleString(swedish ? 'sv-SE' : 'en-US').replace(/\s/g, NBSP);

// "mars 2022" from the sale's date; "kvartal 1 2022" from its period when there's no date.
export const saleWhen = (sale, swedish) => {
    const date = String(sale?.date || '');
    if (/^\d{4}-\d{2}/.test(date)) {
        const day = new Date(`${date.slice(0, 10)}T12:00:00`);
        if (!Number.isNaN(day.getTime())) return day.toLocaleDateString(swedish ? 'sv-SE' : 'en-GB', { month: 'long', year: 'numeric' });
    }
    const quarter = /^(\d{4})Q([1-4])$/.exec(String(sale?.period || ''));
    if (quarter) return swedish ? `kvartal ${quarter[2]} ${quarter[1]}` : `Q${quarter[2]} ${quarter[1]}`;
    return '';
};

const formatRooms = (rooms, swedish) => {
    const n = Number(rooms);
    if (!Number.isFinite(n) || n <= 0) return '';
    const text = Number.isInteger(n) ? String(n) : String(n).replace('.', swedish ? ',' : '.');
    return `${text}${NBSP}${swedish ? 'rum' : (n === 1 ? 'room' : 'rooms')}`;
};

function SaleTip({ sale, since, swedish, anchor }) {
    const ref = useRef(null);
    const [pos, setPos] = useState(null);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el || !anchor) return;
        const { width, height } = el.getBoundingClientRect();
        const centre = anchor.left + anchor.width / 2;
        const left = Math.min(Math.max(EDGE, centre - width / 2), window.innerWidth - width - EDGE);
        const above = anchor.top - GAP - height;
        const below = above < EDGE;
        setPos({ left, top: below ? anchor.bottom + GAP : above, below, arrow: Math.min(Math.max(12, centre - left), width - 12) });
    }, [anchor, sale, since]);

    const when = saleWhen(sale, swedish);
    const details = [sale.sqmPrice > 0 ? `${formatInt(sale.sqmPrice, swedish)}${NBSP}kr/m²` : '', formatRooms(sale.rooms, swedish)].filter(Boolean).join(' · ');
    const up = since && since.pct >= 0;
    return ReactDOM.createPortal(
        <div ref={ref} role="tooltip" className={`sale-tip${pos?.below ? ' is-below' : ''}`}
            style={pos ? { left: pos.left, top: pos.top, '--sale-tip-arrow': `${pos.arrow}px` } : { left: -9999, top: -9999 }}>
            <span className="sale-tip-when">{swedish ? 'Såld' : 'Sold'} {when}</span>
            {sale.price > 0 && <b className="sale-tip-price">{formatInt(sale.price, swedish)}{NBSP}kr</b>}
            {details && <span className="sale-tip-details">{details}</span>}
            {since && Number.isFinite(since.pct) && (
                <span className={`sale-tip-since${up ? '' : ' is-down'}`}>
                    {since.label} <b>{up ? '+' : '−'}{Math.abs(since.pct)}{NBSP}%</b>{since.suffix ? ` ${since.suffix}` : ''}
                </span>
            )}
        </div>,
        document.body,
    );
}

export default function SaleDot({ sale, since, swedish, style, className = '' }) {
    const ref = useRef(null);
    const [anchor, setAnchor] = useState(null);
    const hoverRef = useRef(false);
    const show = useCallback(() => {
        if (ref.current) setAnchor(ref.current.getBoundingClientRect());
    }, []);
    const hide = useCallback(() => setAnchor(null), []);

    // While open: a tap elsewhere, a scroll or a resize closes it (its place on screen would be stale).
    useEffect(() => {
        if (!anchor) return undefined;
        const onDown = (event) => { if (!ref.current?.contains(event.target)) hide(); };
        const onMove = () => hide();
        document.addEventListener('pointerdown', onDown, true);
        window.addEventListener('scroll', onMove, true);
        window.addEventListener('resize', onMove);
        return () => {
            document.removeEventListener('pointerdown', onDown, true);
            window.removeEventListener('scroll', onMove, true);
            window.removeEventListener('resize', onMove);
        };
    }, [anchor, hide]);

    const when = saleWhen(sale, swedish);
    const label = `${swedish ? 'Såld' : 'Sold'} ${when}${sale.price > 0 ? `, ${formatInt(sale.price, swedish)} kr` : ''}`;
    return (
        <>
            <button
                ref={ref}
                type="button"
                className={`sale-dot ${className}${anchor ? ' is-open' : ''}`}
                style={style}
                aria-label={label}
                onPointerEnter={(event) => { if (event.pointerType === 'mouse') { hoverRef.current = true; show(); } }}
                onPointerLeave={(event) => { if (event.pointerType === 'mouse') { hoverRef.current = false; hide(); } }}
                // Keyboard focus only: a tap focuses the button too, and its click would then toggle it shut.
                onFocus={(event) => { if (event.target.matches?.(':focus-visible')) show(); }}
                onBlur={hide}
                onClick={(event) => {
                    event.stopPropagation();
                    if (hoverRef.current) return; // a mouse already shows it on hover
                    if (anchor) hide(); else show();
                }}
            />
            {anchor && <SaleTip sale={sale} since={since} swedish={swedish} anchor={anchor} />}
        </>
    );
}
